// src/components/RecipeNutritionalModal.tsx
// COMPLETELY NEW APPROACH - Standalone implementation without shared components

import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Loader, X, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { buildAssessment, Assessment } from '@/services/dietaryService';
import { NutritionalAnalysisStorage } from '@/lib/nutritionalAnalysisStorage';
import { spendCoins, getCostForAction } from '@/services/chefcoinService';

interface RecipeNutritionalModalProps {
  recipe: {
    id: string;
    title: string;
    ingredients: string;
    method?: string;
  };
  isOpen: boolean;
  onClose: () => void;
}

// P2.6: macros are stored in GRAMS. Percentage is grams / total macro grams.
const macroGramSum = (m: { protein: number; fat: number; complexCarbs: number; simpleCarbs: number }) =>
  (m.protein || 0) + (m.fat || 0) + (m.complexCarbs || 0) + (m.simpleCarbs || 0);
const pctOf = (value: number, total: number): string =>
  total > 0 ? `${Math.round(((value || 0) / total) * 100)}%` : '—';
const round1 = (value: number): string => `${Math.round((value || 0) * 10) / 10}`;
const deriveKcal = (m: { protein: number; fat: number; complexCarbs: number; simpleCarbs: number }): number =>
  Math.round((m.protein || 0) * 4 + (m.fat || 0) * 9 + ((m.complexCarbs || 0) + (m.simpleCarbs || 0)) * 4);

const MacroStat: React.FC<{ value: string; label: string; tone: string }> = ({ value, label, tone }) => (
  <div className="text-center min-w-0">
    <div className={`text-2xl font-bold tabular-nums ${tone}`}>{value}</div>
    <div className="text-sm text-slate-600">{label}</div>
  </div>
);

// Simple status icon component
const StatusIcon: React.FC<{ status: string }> = ({ status }) => {
  switch (status) {
    case 'Compliant':
      return <CheckCircle className="h-5 w-5 text-green-500" />;
    case 'Consideration':
      return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
    case 'NotCompliant':
      return <XCircle className="h-5 w-5 text-red-500" />;
    default:
      return <div className="h-5 w-5 rounded-full bg-gray-300" />;
  }
};

// Simple compliance item component
const ComplianceItem: React.FC<{
  label: string;
  status: string;
  reason: string;
}> = ({ label, status, reason }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div className="relative">
      <div
        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        onClick={() => setShowTooltip(!showTooltip)}
      >
        <span className="font-medium text-gray-900 dark:text-white">{label}</span>
        <StatusIcon status={status} />
      </div>

      {showTooltip && (
        <div className="absolute z-10 mt-2 p-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-w-xs">
          <p className="text-sm text-gray-700 dark:text-gray-300">{reason}</p>
        </div>
      )}
    </div>
  );
};

const RecipeNutritionalModal: React.FC<RecipeNutritionalModalProps> = ({
  recipe,
  isOpen,
  onClose
}) => {
  const { chefcoins_balance, setChefcoinsBalance, user } = useAuth();
  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ANALYSIS_COST = getCostForAction('NUTRITIONAL_ANALYSIS');

  // Check for cached analysis when modal opens
  useEffect(() => {
    if (isOpen && recipe) {
      const cachedAnalysis = NutritionalAnalysisStorage.getAnalysis(recipe.id);
      if (cachedAnalysis) {
        setAssessment(cachedAnalysis);
      } else {
        setAssessment(null);
      }
      setError(null);
    }
  }, [isOpen, recipe]);

  const handleAnalyze = async () => {
    if ((chefcoins_balance || 0) < ANALYSIS_COST) {
      setError('Insufficient ChefCoins for analysis');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Deduct coins
      const { success } = await spendCoins(user?.id || '', ANALYSIS_COST, { feature: 'NUTRITIONAL_ANALYSIS' });
      if (success) {
        setChefcoinsBalance((chefcoins_balance || 0) - ANALYSIS_COST);

        // Perform analysis
        const ingredientsArray = recipe.ingredients.split('\n').filter(line => line.trim());
        const methodArray = recipe.method ? recipe.method.split('\n').filter(line => line.trim()) : [];

        const result = await buildAssessment(ingredientsArray, methodArray, 1);

        // Save to cache
        NutritionalAnalysisStorage.saveAnalysis(recipe.id, result);
        setAssessment(result);
      } else {
        setError('Failed to process payment');
      }
    } catch {
      setError('Analysis failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[100dvh] sm:max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <div className="relative">
            <button
              onClick={onClose}
              className="absolute top-0 right-0 z-20 p-2 h-10 w-10 rounded-full text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors flex items-center justify-center"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>

          <DialogHeader className="pr-10">
            <DialogTitle className="text-2xl font-bold text-slate-900">
              Nutritional Analysis: {recipe.title}
            </DialogTitle>
          </DialogHeader>

          <div className="mt-6">
            {!assessment ? (
              // Analysis prompt
              <div className="text-center py-8">
                <div className="mb-6">
                  <div className="w-16 h-16 mx-auto bg-blue-100 rounded-full flex items-center justify-center mb-4">
                    <span className="text-2xl">🔬</span>
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-slate-900">Analyze Recipe Nutrition</h3>
                  <p className="text-slate-600 max-w-md mx-auto">
                    Get detailed nutritional insights including macronutrients, dietary compliance, and recommendations.
                  </p>
                </div>

                <Button
                  onClick={handleAnalyze}
                  disabled={isLoading || (chefcoins_balance || 0) < ANALYSIS_COST}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg min-h-[48px]"
                >
                  {isLoading ? (
                    <>
                      <Loader className="animate-spin h-5 w-5 mr-2" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      Analyze for {ANALYSIS_COST} 🪙
                    </>
                  )}
                </Button>

                <p className="mt-3 text-sm text-slate-500">
                  Balance: {chefcoins_balance || 0} 🪙
                </p>

                {error && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-700">{error}</p>
                  </div>
                )}
              </div>
            ) : (
              // Analysis results
              <div className="space-y-6">
                {/* Macronutrients Summary */}
                <Card className="p-4">
                  <h3 className="text-lg font-semibold mb-4 text-slate-900">Macronutrient Breakdown</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <MacroStat value={pctOf(assessment.macros.protein, macroGramSum(assessment.macros))} label="Protein" tone="text-blue-600" />
                    <MacroStat value={pctOf(assessment.macros.fat, macroGramSum(assessment.macros))} label="Fat" tone="text-green-600" />
                    <MacroStat value={pctOf(assessment.macros.complexCarbs + assessment.macros.simpleCarbs, macroGramSum(assessment.macros))} label="Carbs" tone="text-orange-600" />
                  </div>
                  <p className="mt-3 text-xs text-slate-500">
                    Calories: <span className="font-semibold text-slate-700">{deriveKcal(assessment.macros)} kcal</span>
                    {' '}· Protein <span className="font-semibold text-slate-700">{round1(assessment.macros.protein)}g</span>
                    {' '}· Fat <span className="font-semibold text-slate-700">{round1(assessment.macros.fat)}g</span>
                    {' '}· Carbs <span className="font-semibold text-slate-700">{round1(assessment.macros.complexCarbs + assessment.macros.simpleCarbs)}g</span>
                    {assessment.macros.fiber > 0 && <> · Fiber <span className="font-semibold text-slate-700">{round1(assessment.macros.fiber)}g</span></>}
                  </p>
                </Card>

                {/* Dietary Compliance Grid */}
                <Card className="p-4">
                  <h3 className="text-lg font-semibold mb-4 text-slate-900">Dietary Compliance</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <ComplianceItem
                      label="Vegetarian"
                      status={assessment.dietaryFlags.Vegetarian.status}
                      reason={assessment.dietaryFlags.Vegetarian.reason}
                    />
                    <ComplianceItem
                      label="Vegan"
                      status={assessment.dietaryFlags.Vegan.status}
                      reason={assessment.dietaryFlags.Vegan.reason}
                    />
                    <ComplianceItem
                      label="Gluten-Free"
                      status={assessment.dietaryFlags.GlutenFree.status}
                      reason={assessment.dietaryFlags.GlutenFree.reason}
                    />
                    <ComplianceItem
                      label="Dairy-Free"
                      status={assessment.dietaryFlags.DairyFree.status}
                      reason={assessment.dietaryFlags.DairyFree.reason}
                    />
                    <ComplianceItem
                      label="Low-Carb"
                      status={assessment.dietaryFlags.LowCarb.status}
                      reason={assessment.dietaryFlags.LowCarb.reason}
                    />
                    <ComplianceItem
                      label="Carb Quality"
                      status={assessment.dietaryFlags.CarbQuality.status}
                      reason={assessment.dietaryFlags.CarbQuality.reason}
                    />
                    <ComplianceItem
                      label="Glucose-Friendly"
                      status={assessment.dietaryFlags.GlucoseFriendly.status}
                      reason={assessment.dietaryFlags.GlucoseFriendly.reason}
                    />
                    <ComplianceItem
                      label="Healthy & Light"
                      status={assessment.dietaryFlags.HealthyLight.status}
                      reason={assessment.dietaryFlags.HealthyLight.reason}
                    />
                  </div>
                </Card>

                {/* Contextual Notes */}
                {assessment.contextualNotes && assessment.contextualNotes.length > 0 && (
                  <Card className="p-4 bg-blue-50 border-blue-200">
                    <h3 className="text-lg font-semibold mb-3 text-blue-900">📝 Contextual Notes</h3>
                    <div className="space-y-2">
                      {assessment.contextualNotes.map((note, index) => (
                        <p key={index} className="text-blue-800 text-sm"
                          dangerouslySetInnerHTML={{ __html: note.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
                      ))}
                    </div>
                  </Card>
                )}

                {/* Re-analyze button */}
                <div className="text-center">
                  <Button
                    onClick={() => setAssessment(null)}
                    variant="outline"
                    className="px-4 py-2"
                  >
                    Re-analyze ({ANALYSIS_COST} 🪙)
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RecipeNutritionalModal;
